import React, { useState, useEffect } from "react";
import { ChevronDown, CreditCard, Info } from "lucide-react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

/**
 * Re-usable installment-plan configuration block for the
 * "create / edit ticket type" forms. Pure controlled component:
 * pass `value` + `onChange(patch)` and it manages local UI state only.
 */
export const InstallmentConfigSection = ({ value, onChange, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const v = value || {};

  const set = (patch) => onChange({ ...v, ...patch });

  // Toggle plan number in the suggested_plans array
  const togglePlan = (n) => {
    const cur = Array.isArray(v.suggested_plans) ? v.suggested_plans : [2, 3, 4];
    const next = cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n].sort((a, b) => a - b);
    set({ suggested_plans: next });
  };

  const setReminders = (raw) => {
    // Accept "7,3,1" → [7, 3, 1]
    const days = (raw || "")
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n >= 0);
    set({ reminder_days_before: days });
  };

  const computedRemindersStr = Array.isArray(v.reminder_days_before)
    ? v.reminder_days_before.join(", ")
    : "7, 3, 1";

  // Local raw text so the user can type commas/spaces freely without
  // the parser stripping intermediate states (e.g. "7," → "7" loses comma).
  const [remindersRaw, setRemindersRaw] = useState(computedRemindersStr);
  // Re-sync local text when value updates from outside (e.g. opening a different ticket).
  useEffect(() => {
    setRemindersRaw(computedRemindersStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedRemindersStr]);

  const remindersStr = remindersRaw;

  return (
    <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-amber-500/5"
        data-testid="installments-section-toggle"
      >
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-amber-400" />
          <span className="text-white font-medium text-sm">Pagamento Faseado (parcelas)</span>
          {v.installments_enabled && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Activado</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-amber-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-amber-500/10 pt-4">
          {/* Main toggle */}
          <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2.5">
            <div>
              <p className="text-sm text-white font-medium">Permitir compra a prazo</p>
              <p className="text-xs text-zinc-400">Activa o pagamento faseado para este tipo de bilhete.</p>
            </div>
            <Switch
              checked={!!v.installments_enabled}
              onCheckedChange={(x) => set({ installments_enabled: x })}
              data-testid="installments-enabled-toggle"
            />
          </div>

          {v.installments_enabled && (
            <>
              {/* Plan suggestions */}
              <div>
                <Label className="text-zinc-300 text-sm">Planos sugeridos no checkout</Label>
                <p className="text-xs text-zinc-500 mb-2">O cliente vê estes atalhos. Pode sempre escolher valor livre.</p>
                <div className="flex gap-2 flex-wrap">
                  {[2, 3, 4, 5, 6].map((n) => {
                    const sel = (v.suggested_plans || []).includes(n);
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => togglePlan(n)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          sel
                            ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
                            : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                        }`}
                        data-testid={`plan-${n}x`}
                      >
                        {n}x
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Min first payment & min installment */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-sm">% Mínima da 1ª prestação</Label>
                  <div className="relative mt-1.5">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={v.min_first_payment_percent ?? 25}
                      onChange={(e) => set({ min_first_payment_percent: parseFloat(e.target.value) || 0 })}
                      className="input-dark pr-8"
                      data-testid="installments-min-first-pct"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Mínimo por prestação (€)</Label>
                  <div className="relative mt-1.5">
                    <Input
                      type="number"
                      min="0"
                      step="0.50"
                      value={v.min_installment_amount ?? 10}
                      onChange={(e) => set({ min_installment_amount: parseFloat(e.target.value) || 0 })}
                      className="input-dark pr-8"
                      data-testid="installments-min-amount"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">€</span>
                  </div>
                </div>
              </div>

              {/* Surcharge (%) — applied only when buyer picks installments */}
              <div>
                <Label className="text-zinc-300 text-sm">Acréscimo por prestações (taxa)</Label>
                <div className="relative mt-1.5">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={v.installment_surcharge_pct ?? ""}
                    onChange={(e) => set({ installment_surcharge_pct: e.target.value === "" ? null : parseFloat(e.target.value) })}
                    className="input-dark pr-8"
                    placeholder="Usar valor global"
                    data-testid="installments-surcharge-pct"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
                </div>
                <p className="text-zinc-500 text-xs mt-1">Deixe vazio para usar a taxa global definida em Configurações Gerais. Só é aplicado quando o comprador escolhe pagar em prestações.</p>
              </div>

              {/* Deadline */}
              <div>
                <Label className="text-zinc-300 text-sm">Prazo limite para concluir</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <Select
                    value={v.installment_deadline_type || "before_session"}
                    onValueChange={(x) => set({ installment_deadline_type: x })}
                  >
                    <SelectTrigger className="input-dark" data-testid="installments-deadline-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 text-white">
                      <SelectItem value="before_session">Antes da sessão</SelectItem>
                      <SelectItem value="until_session">Até à data da sessão</SelectItem>
                      <SelectItem value="no_deadline">Sem prazo</SelectItem>
                    </SelectContent>
                  </Select>
                  {v.installment_deadline_type === "before_session" && (
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        value={v.installment_deadline_days ?? 7}
                        onChange={(e) => set({ installment_deadline_days: parseInt(e.target.value, 10) || 0 })}
                        className="input-dark pr-12"
                        data-testid="installments-deadline-days"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">dias</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Seat lock policy */}
              <div>
                <Label className="text-zinc-300 text-sm">Bloqueio do lugar</Label>
                <Select
                  value={v.seat_lock_policy || "from_first"}
                  onValueChange={(x) => set({ seat_lock_policy: x })}
                >
                  <SelectTrigger className="input-dark mt-1.5" data-testid="installments-seat-lock">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="from_first">Reservar desde a 1ª prestação</SelectItem>
                    <SelectItem value="after_full">Só após pagamento completo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Cancellation policy */}
              <div>
                <Label className="text-zinc-300 text-sm">Se o comprador cancelar</Label>
                <Select
                  value={v.cancellation_policy || "partial_refund"}
                  onValueChange={(x) => set({ cancellation_policy: x })}
                >
                  <SelectTrigger className="input-dark mt-1.5" data-testid="installments-cancel-policy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="full_refund">Reembolso total</SelectItem>
                    <SelectItem value="partial_refund">Reembolso parcial (com taxa)</SelectItem>
                    <SelectItem value="forfeit">Sem reembolso</SelectItem>
                    <SelectItem value="credit">Crédito para outro evento</SelectItem>
                  </SelectContent>
                </Select>
                {v.cancellation_policy === "partial_refund" && (
                  <div className="mt-2">
                    <Label className="text-zinc-400 text-xs">Taxa retida (%)</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={v.cancellation_fee_percent ?? 10}
                        onChange={(e) => set({ cancellation_fee_percent: parseFloat(e.target.value) || 0 })}
                        className="input-dark pr-8"
                        data-testid="installments-cancel-fee"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Expired policy */}
              <div>
                <Label className="text-zinc-300 text-sm">Se ultrapassar o prazo sem pagar</Label>
                <Select
                  value={v.expired_policy || "auto_cancel"}
                  onValueChange={(x) => set({ expired_policy: x })}
                >
                  <SelectTrigger className="input-dark mt-1.5" data-testid="installments-expired-policy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="auto_cancel">Cancelar automaticamente (aplicar política de cancelamento)</SelectItem>
                    <SelectItem value="freeze_keep_credit">Congelar plano e manter crédito</SelectItem>
                    <SelectItem value="manual">Manter activo (admin decide caso a caso)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reminders */}
              <div>
                <Label className="text-zinc-300 text-sm">Lembretes automáticos</Label>
                <Input
                  value={remindersStr}
                  onChange={(e) => setRemindersRaw(e.target.value)}
                  onBlur={() => setReminders(remindersRaw)}
                  placeholder="7, 3, 1"
                  className="input-dark mt-1.5"
                  data-testid="installments-reminders"
                />
                <p className="text-xs text-zinc-500 mt-1 flex items-start gap-1.5">
                  <Info className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>Dias antes do prazo para enviar lembrete (separados por vírgula). Ex: 7, 3, 1 → envia 7 dias, 3 dias e 1 dia antes.</span>
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
